import React from 'react'
import { IconLoader2 } from '@tabler/icons-react'
import type { ButtonProps, ButtonSize } from './Button.types'
import './Button.css'

const SPINNER_SIZE: Record<ButtonSize, number> = {
  xs: 14,
  sm: 14,
  md: 16,
  lg: 20,
}

const Button: React.FC<ButtonProps> = ({
  children,
  label = 'Button',
  variant = 'primary',
  size = 'md',
  destructive = false,
  dark = false,
  darkMode,
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className = '',
  style,
  previewState,
  disabled,
  type = 'button',
  'aria-label': ariaLabel,
  ...rest
}) => {
  const content = children ?? label
  const isLoading = loading || previewState === 'loading'
  const isDisabled = disabled || previewState === 'disabled' || isLoading
  const isPreview = previewState != null
  const isDark = darkMode ?? dark
  /** Destructive styles exist for primary/secondary only. */
  const isDestructive =
    destructive && (variant === 'primary' || variant === 'secondary')
  const accessibleName =
    ariaLabel ??
    (isLoading && typeof content === 'string' ? content : undefined)

  const classNames = [
    'ds-button',
    `ds-button--${variant}`,
    `ds-button--${size}`,
    isDestructive ? 'ds-button--destructive' : '',
    isDark ? 'ds-button--dark' : '',
    fullWidth ? 'ds-button--full-width' : '',
    isLoading ? 'ds-button--loading' : '',
    previewState ? `ds-button--preview-${previewState}` : '',
    isPreview ? 'ds-button--preview' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type={type}
      className={classNames}
      style={style}
      {...rest}
      disabled={isDisabled}
      aria-busy={isLoading || undefined}
      aria-disabled={isDisabled || undefined}
      aria-label={accessibleName}
      data-preview-state={previewState}
    >
      <span className="ds-button__inner">
        {isLoading ? (
          <IconLoader2
            className="ds-button__spinner"
            size={SPINNER_SIZE[size]}
            stroke={2}
            aria-hidden="true"
          />
        ) : null}
        <span
          className={`ds-button__content${
            isLoading ? ' ds-button__content--loading' : ''
          }`}
        >
          {leftIcon ? (
            <span className="ds-button__icon ds-button__icon--left">
              {leftIcon}
            </span>
          ) : null}
          <span className="ds-button__label">{content}</span>
          {rightIcon ? (
            <span className="ds-button__icon ds-button__icon--right">
              {rightIcon}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  )
}

export default Button
